// @ds/editable/Editable — unified single-field inline-edit primitive.
//
// Merges two legacy sources:
//   - src/components/ui/Editable.tsx      (modal / dropdown variants)
//   - src/components/editable/EditableField.tsx (discriminated FieldType + value coercion)
//
// Design contract:
//   - `value` + `onChange` for read/write lifecycle.
//   - `field` — discriminated union describing the input type.
//   - Edit lifecycle: click to edit, blur/Enter commits, Escape cancels.
//   - Resting state is a Field-like label+value with a HoverShell affordance.
//   - Auto-emits `data-testid="field-{name}"` on the root; `field-{name}-input` on the
//     active editor; `field-{name}-save` / `field-{name}-cancel` on inline controls.
//
// Supported field types:
//   text · textarea · number · weight · currency · date · boolean
//   select · multiselect · entity-select

import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Tooltip } from "antd";
import { EntityLink } from "@ds/connected/EntityLink";
import {
  renderFieldWidget,
  resolveOptionLabels,
} from "@ds/editable/fieldTypeRegistry";
import { useTranslation } from "react-i18next";
import type { EntityType } from "@ds/navigation/navigation";
import { LoadingOutlined, LockOutlined } from "@ant-design/icons";
import { HoverShell } from "@ds/editable/HoverShell";
import { LabelAffordanceContext } from "@ds/editable/affordanceContext";
import { TextValue } from "@ds/display/TextValue";
import { Box } from "@ds/layout/Box";
import { errorText } from "@shared/errorText";
import {
  FONT_FAMILY,
  FONT_SIZE,
  FONT_WEIGHT,
  MOTION,
  RADIUS,
  SHADOW,
  SPACE,
  SURFACE,
  TEXT,
  TONE,
} from "@ds/tokens/palette";

// ─── FieldType — discriminated union ─────────────────────────────────────────
//
// Single source of truth lives in `@ds/editable/editFieldSpec`. Re-exported
// here under both the legacy `EditableFieldType` name (broadly imported by
// `<Editable>` consumers) and `EditFieldSpec` (the canonical schema-rewrite
// name on `FieldDef.edit.field`).

export type { EditFieldSpec, EditFieldSpec as EditableFieldType, FieldOption } from "@ds/editable/editFieldSpec";
import type { EditFieldSpec as EditableFieldType } from "@ds/editable/editFieldSpec";
import { EditableGroupContext } from "@ds/editable/editableGroupContext";

export type { GroupEditCtx } from "@ds/editable/editableGroupContext";

// ─── Inputs ───────────────────────────────────────────────────────────────────

export interface EditableInput<T = unknown> {
  /** Stable name — used to compute `field-{name}` testids. */
  name: string;
  /** Current value. May be null. */
  value: T | null;
  /** Discriminated field config — controls the edit widget rendered. */
  field: EditableFieldType;
  /** Required field: shows a `*` indicator and blocks save of empty values. */
  required?: boolean;
  /** Custom display renderer. When provided, replaces the default
   *  string-or-ReactNode value display in resting state. */
  display?: (v: T | null) => React.ReactNode;
  /** Placeholder shown in resting state when value is null/empty. */
  empty?: React.ReactNode;
  /** Explicit read-only override. */
  readOnly?: boolean;
  /**
   * External error string. When set, shows the field in error state even
   * in display mode (tooltip + red border).
   */
  error?: string | null;
  /**
   * Validation callback. Called with the candidate value BEFORE the empty
   * check and before onChange fires. Return a string to show as an error and
   * block commit; return null to allow.
   */
  validate?: (v: T | null) => string | null;
  /** Custom root testid. Defaults to `field-{name}`. */
  testId?: string;
  /** Inline width — number = px, string = any CSS width. */
  width?: number | string;
}

// ─── Outputs ─────────────────────────────────────────────────────────────────

export interface EditableOutput<T = unknown> {
  /** Called with the new value when the user commits an edit. May be async.
   *  If it rejects, the field returns to edit state and shows the error. */
  onChange: (v: T | null) => void | Promise<void>;
}

// No side-actions for Editable — the edit lifecycle is its own concern.

export type EditableProps<T = unknown> = EditableInput<T> & EditableOutput<T>;

// ─── Component ────────────────────────────────────────────────────────────────

export function Editable<T = unknown>({
  name,
  value,
  field,
  required,
  display,
  empty = "—",
  readOnly = false,
  error: externalError,
  validate,
  testId,
  width,
  onChange,
}: EditableProps<T>) {
  type State = "display" | "editing" | "saving";

  const { editing: groupEditing, saveCount } = useContext(EditableGroupContext);
  const labelHostsAffordance = useContext(LabelAffordanceContext);
  const lastSaveCount = useRef(saveCount);
  const { t } = useTranslation();
  const [state, setState] = useState<State>("display");
  const [draft, setDraft] = useState<T | null>(value);
  const [internalError, setInternalError] = useState<string | null>(null);
  // Brief "just-saved" highlight — keeps HoverShell's surfaceHover background
  // visible for ~2.4s after a successful commit so the operator sees what changed.
  const [justSaved, setJustSaved] = useState(false);
  const justSavedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootTestId = testId ?? `field-${name}`;
  const error = externalError ?? internalError;

  useEffect(() => () => {
    if (justSavedTimer.current) clearTimeout(justSavedTimer.current);
  }, []);

  const flashJustSaved = () => {
    if (justSavedTimer.current) clearTimeout(justSavedTimer.current);
    setJustSaved(true);
    justSavedTimer.current = setTimeout(() => setJustSaved(false), 2400);
  };

  // Sync draft when value changes in display mode.
  useEffect(() => {
    if (state === "display") setDraft(value);
  }, [value, state]);

  // Participate in group edit context.
  useEffect(() => {
    if (readOnly) return;
    if (groupEditing && state === "display") {
      setDraft(value);
      setState("editing");
    }
    if (!groupEditing && state === "editing") {
      if (saveCount > lastSaveCount.current) {
        void commitValue(draft);
      } else {
        // cancel
        setDraft(value);
        setInternalError(null);
        setState("display");
      }
      lastSaveCount.current = saveCount;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupEditing, saveCount]);

  const enterEdit = () => {
    if (readOnly) return;
    setDraft(value);
    setInternalError(null);
    setState("editing");
  };

  const cancel = () => {
    setDraft(value);
    setInternalError(null);
    setState("display");
  };

  const commitValue = async (next: T | null = draft) => {
    if (Object.is(next, value)) {
      setState("display");
      return;
    }
    // validate callback fires first — before the required/empty check.
    if (validate) {
      const msg = validate(next);
      if (msg !== null) {
        setInternalError(msg);
        return;
      }
    }
    const isEmpty =
      next === null ||
      next === undefined ||
      (typeof next === "string" && next.trim() === "");
    if (required && isEmpty) {
      setInternalError("To pole jest wymagane");
      return;
    }
    setState("saving");
    try {
      await onChange(next);
      setInternalError(null);
      setState("display");
      flashJustSaved();
    } catch (e) {
      const msg = errorText(e) ?? String(e);
      setInternalError(msg);
      setState("editing");
    }
  };

  // ── Display value renderer ────────────────────────────────────────────────

  const renderDisplayValue = (): React.ReactNode => {
    if (display) return display(value);
    if (value === null || value === undefined || value === "") {
      return (
        <TextValue tone="faint" italic>
          {typeof empty === "string" ? empty : empty}
        </TextValue>
      );
    }
    if (field.type === "boolean") {
      return (value as unknown as boolean) ? "Tak" : "Nie";
    }
    if (field.type === "select") {
      const opt = field.options.find((o) => o.value === value);
      return opt?.label ?? String(value);
    }
    if (field.type === "multiselect") {
      const vals = Array.isArray(value) ? (value as (string | number)[]) : [];
      return vals
        .map((v) => {
          const opt = field.options.find((o) => o.value === v);
          return opt?.label ?? String(v);
        })
        .join(", ");
    }
    if (field.type === "entity-select") {
      const id = value as number | null;
      const type = (field.entityType ?? field.kind) as EntityType;
      const label = field.displayLabel;
      if (id && label) {
        return <EntityLink type={type} id={id} label={label} variant="inline" />;
      }
      if (id) return String(id);
      return null;
    }
    if (Array.isArray(value)) return (value as unknown[]).join(", ");
    return String(value);
  };

  // ── Editor renderer ───────────────────────────────────────────────────────

  const inGroup = groupEditing;
  const inputTestId = `${rootTestId}-input`;

  // Per-type commit lifecycle. Widgets with a definitive "selection event"
  // (date pickers, select dropdowns, radio groups) commit immediately on
  // change — no blur firing. Text-like widgets accumulate edits and
  // commit on blur or Enter.
  const COMMIT_ON_CHANGE = new Set<EditableFieldType["type"]>([
    "date",
    "time",
    "select",
    "entity-select",
    "radio-group",
    "country-select",
  ]);
  /** Widgets where blur means "cancel" (the user clicked outside the
   *  dropdown without picking — preserves the original value). For
   *  text-like widgets, blur commits. */
  const BLUR_CANCELS = new Set<EditableFieldType["type"]>([
    "select",
    "entity-select",
    "country-select",
  ]);

  const handleEditorChange = (v: unknown) => {
    const next = v as T;
    setDraft(next);
    if (COMMIT_ON_CHANGE.has(field.type) && !inGroup) {
      void commitValue(next);
    }
  };

  const handleEditorBlur = () => {
    if (BLUR_CANCELS.has(field.type)) {
      cancel();
    } else if (!inGroup) {
      void commitValue(draft);
    }
  };

  // Enter-to-commit for single-line text-like widgets only — textarea
  // would lose multi-line editing if Enter committed.
  const handleEditorKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
      return;
    }
    if (
      e.key === "Enter" &&
      !COMMIT_ON_CHANGE.has(field.type) &&
      field.type !== "textarea" &&
      !inGroup
    ) {
      e.preventDefault();
      void commitValue(draft);
    }
  };

  /** Resolved per-type placeholder — preserved from the legacy inline-edit
   *  behavior (text/textarea/select-like widgets read their own
   *  field.placeholder; other types had none). The registry's widgets honor
   *  this via ctx.placeholder. */
  const inlinePlaceholder =
    "placeholder" in field ? (field.placeholder) : undefined;

  // Single dispatcher call — both inline-edit and form contexts go
  // through `renderFieldWidget`. Per-type commit lifecycle is handled
  // above via COMMIT_ON_CHANGE / BLUR_CANCELS sets; the widget itself
  // is the same one the form-context dispatcher renders.
  //
  // `resolveOptionLabels` translates select/multiselect/radio-group
  // option labels from i18n keys before the widget sees them — same
  // transform SchemaFormItem applies in form context, so option text
  // is consistent across both.
  const renderEditor = (): React.ReactNode =>
    renderFieldWidget(
      resolveOptionLabels(field, (k) => t(k, k) as unknown as string),
      {
        value: draft,
        onChange: handleEditorChange,
        placeholder: inlinePlaceholder,
        autoFocus: true,
        size: "small",
        status: error ? "error" : undefined,
        testId: inputTestId,
        onKeyDown: handleEditorKeyDown,
        onBlur: handleEditorBlur,
        defaultOpen: COMMIT_ON_CHANGE.has(field.type),
      },
    );

  // ── Shared container style ────────────────────────────────────────────────

  const containerStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: SPACE.xs,
    width: width ?? "100%",
    minHeight: SPACE.xl,
    fontFamily: FONT_FAMILY.sans,
    fontSize: FONT_SIZE.normal,
    fontWeight: FONT_WEIGHT.semibold,
  };

  // ── Saving ────────────────────────────────────────────────────────────────

  if (state === "saving") {
    return (
      // Bespoke leaf chrome: containerStyle bakes inline-flex + width +
      // minHeight + font sizing as a single edit-affordance container;
      // negative margin/padding ("3px 6px" / "-3px -6px") is off-scale.
      // No closed-enum Box composition expresses this exact affordance.
      <div
        data-testid={rootTestId}
        style={{
          ...containerStyle,
          padding: "3px 6px",
          margin: "-3px -6px",
        }}
      >
        {/* Bespoke leaf chrome: 0.6 opacity dimming during saving — no closed-enum
            tone captures partial opacity. flex:1 + minWidth:0 via Box grow. */}
        <Box as="span" grow>
          <span style={{ opacity: 0.6 }}>
            {renderDisplayValue()}
          </span>
        </Box>
        <LoadingOutlined
          style={{ fontSize: FONT_SIZE.small, color: TEXT.muted }}
        />
      </div>
    );
  }

  // ── Editing ───────────────────────────────────────────────────────────────

  if (state === "editing") {
    const editor = (
      // Bespoke leaf chrome: same containerStyle affordance as the saving branch.
      <div
        data-testid={rootTestId}
        style={{ ...containerStyle, padding: 0 }}
      >
        {/* Editor grows to fill the row so wide widgets (multi-select tag rows,
            text) aren't squeezed to content width beside the commit buttons. */}
        <Box grow>{renderEditor()}</Box>
        {/* Inline save/cancel for non-group edits */}
        {!inGroup && (
          // Bespoke leaf chrome: shrink-locked inline cluster wrapping 18×18
          // commit buttons. Box `shrink` covers flexShrink:0 but the buttons
          // themselves require off-scale dimensions, so the cluster is kept raw.
          <Box shrink>
            <button
              data-testid={`${rootTestId}-save`}
              // mousedown.preventDefault stops the editor input from blurring
              // before our click handler runs — without this, handleEditorBlur
              // fires first on text-like widgets and commits/cancels the draft
              // based on type policy, defeating the explicit save/cancel intent.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => void commitValue(draft)}
              style={saveButtonStyle}
              title="Zapisz"
            >
              ✓
            </button>
            <button
              data-testid={`${rootTestId}-cancel`}
              // mousedown.preventDefault — see save button comment above.
              // Without this, clicking Cancel on a text-like field (weight,
              // number, text) commits the unsaved draft via onBlur before
              // cancel() runs, silently saving instead of discarding.
              onMouseDown={(e) => e.preventDefault()}
              onClick={cancel}
              style={cancelButtonStyle}
              title="Anuluj"
            >
              ✕
            </button>
          </Box>
        )}
      </div>
    );
    return error ? (
      <Tooltip title={error} open color={TONE.red.base}>
        {editor}
      </Tooltip>
    ) : (
      editor
    );
  }

  // ── Display ───────────────────────────────────────────────────────────────

  const editable = !readOnly;
  const errorBorder = `1px solid ${TONE.red.base}`;

  // HoverShell owns the click affordance: hover-tracked background, edit-icon
  // opacity escalation, role="button" + Enter/Space keyboard handling.
  const innerStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: SPACE.xs,
    width: "100%",
    minHeight: SPACE.xl,
    fontFamily: FONT_FAMILY.sans,
    fontSize: FONT_SIZE.normal,
    fontWeight: FONT_WEIGHT.semibold,
    borderRadius: RADIUS.xs,
    border: error ? errorBorder : "1px solid transparent",
    background: error ? TONE.red.softBg : undefined,
  };

  const inner = (
    <span
      data-testid={rootTestId}
      style={innerStyle}
    >
      <Box as="span" grow>
        <TextValue tone="primary" wrapWords>
          {renderDisplayValue()}
        </TextValue>
      </Box>
      {required && (
        <TextValue size="sm" lineHeight="tight">
          *
        </TextValue>
      )}
      {readOnly && !labelHostsAffordance && (
        <LockOutlined
          style={{
            fontSize: FONT_SIZE.micro,
            color: TEXT.faint,
            lineHeight: 1,
          }}
        />
      )}
    </span>
  );

  const displayNode = editable ? (
    <HoverShell
      affordance={labelHostsAffordance ? "none" : "edit"}
      onClick={enterEdit}
      disabled={!!error}
      active={justSaved}
      style={{ width: width ?? "100%" }}
    >
      {inner}
    </HoverShell>
  ) : (
    inner
  );

  return error ? (
    <Tooltip title={error} color={TONE.red.base}>
      {displayNode}
    </Tooltip>
  ) : (
    displayNode
  );
}

// ─── Button styles (inline save/cancel) ──────────────────────────────────────
// Off-scale sizing: these tiny commit buttons appear inline with input fields.
// They have no corresponding token because no other component uses this exact
// 18px×18px commit affordance. Inline comment explains the values.

const COMMIT_BTN_BASE: React.CSSProperties = {
  width:  18,  // 18px — editable commit button size; off-scale per Toolbar precedent
  height: 18,  // 18px — editable commit button size; off-scale per Toolbar precedent
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: `1px solid ${SURFACE.border}`,
  borderRadius: RADIUS.xs,
  cursor: "pointer",
  fontSize: FONT_SIZE.micro,
  lineHeight: 1,
  padding: 0,
  flexShrink: 0,
  boxShadow: SHADOW.xs,
  transition: `background ${MOTION.fast} ease`,
};

const saveButtonStyle: React.CSSProperties = {
  ...COMMIT_BTN_BASE,
  background: TONE.blue.base,
  borderColor: TONE.blue.base,
  color: TEXT.inverse,
  fontWeight: FONT_WEIGHT.semibold,
};

const cancelButtonStyle: React.CSSProperties = {
  ...COMMIT_BTN_BASE,
  background: SURFACE.surface,
  color: TEXT.muted,
};

// ─── Re-exports for convenience ───────────────────────────────────────────────

export type { EditableFieldType as FieldType };
